import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MailWarning } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import Turnstile, { TurnstileStatus } from '@/components/Turnstile';
import ResendConfirmation from '@/components/ResendConfirmation';
import { guardAuth, guardMessage, reportAuthFailure, reportAuthSuccess, captchaPendingMessage } from '@/lib/authGuard';
import PurchaseSteps from '@/components/PurchaseSteps';
import { authLink, localizeNext, readPurchaseIntent } from '@/lib/purchaseIntent';

// 16px on phones so iOS Safari does not zoom the page when a field is tapped.
const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-3 text-base text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:outline-none sm:py-2.5 sm:text-sm';
const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

const Login: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { signIn, resetPassword, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const es = lang === 'es';
  const nextParam = params.get('next');
  const buyParam = params.get('buy');
  const buy = buyParam === 'lead' || buyParam === 'plan' ? buyParam : null;
  // Pre-fill the address when a link carries it (post-payment page, sign-up
  // "already registered" hop) so a returning buyer only types the password.
  const [email, setEmail] = useState(() => (params.get('email') || '').trim());
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [showPw, setShowPw] = useState(false);
  /** Set while this page's own sign-in runs — it navigates by itself. */
  const submitting = useRef(false);

  /**
   * ALREADY SIGNED IN? SKIP THE FORM.  (2026-09-30 review)
   * Session-expired links, the "I confirmed — continue" button and old tabs
   * could land a signed-in buyer on an empty sign-in form. Go straight on to
   * the lead / plan they were buying, or the dashboard.
   */
  useEffect(() => {
    if (authLoading || !profile || submitting.current) return;
    navigate(localizeNext(nextParam || (buy ? readPurchaseIntent()?.returnTo : null), path, path('/dashboard')), {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.id]);
  /**
   * TRUE when the sign-in was refused because the ADDRESS is not confirmed —
   * not because the password is wrong. Previously both cases collapsed into
   * "Email or password is incorrect", so anyone who never received the first
   * confirmation email had no way back in without contacting support.
   */
  const [unconfirmed, setUnconfirmed] = useState(false);
  // Turnstile token. Verified SERVER-SIDE by the auth-guard function before any
  // credential is checked — the widget alone is never treated as proof.
  // Tokens are single-use: reset after every server check so a retry (e.g.
  // after a mistyped password) does not re-send a dead token.
  const [captcha, setCaptcha] = useState('');
  const [captchaStatus, setCaptchaStatus] = useState<TurnstileStatus>('loading');
  const [captchaReset, setCaptchaReset] = useState(0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setUnconfirmed(false);
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError(t('auth.errEmail'));
    if (!password) return setError(es ? 'Escriba su contraseña.' : 'Enter your password.');
    // softGate: login only needs a challenge after repeated failures (server-side),
    // so a broken/blocked widget must not lock every user out of signing in.
    const pendingMsg = captchaPendingMessage(captchaStatus, captcha, lang, true);
    if (pendingMsg) return setError(pendingMsg);
    setBusy(true);

    /*
     * NEVER STUCK ON "Loading…". A dropped phone connection used to throw out
     * of this handler and leave the button disabled with no message.
     */
    let res: Awaited<ReturnType<typeof signIn>>;
    try {
      // Brute-force / credential-stuffing gate: per-IP + per-email lockout and
      // server-side bot challenge.
      const verdict = await guardAuth('login', email, captcha);
      setCaptchaReset((n) => n + 1);
      if (!verdict.ok) {
        setBusy(false);
        return setError(guardMessage(verdict, t, lang));
      }
      submitting.current = true;
      res = await signIn(email, password);
    } catch {
      submitting.current = false;
      setBusy(false);
      setCaptchaReset((n) => n + 1);
      return setError(
        es
          ? 'Se perdió la conexión. Revise su señal y toque el botón otra vez.'
          : 'The connection dropped. Check your signal and tap the button again.',
      );
    }
    if (res.error) {
      submitting.current = false;
      // Burn a strike; 5 failures in 15 minutes locks this email + IP out.
      await reportAuthFailure('login', email).catch(() => undefined);
      setBusy(false);
      if (res.unconfirmed) {
        // Specific, recoverable message + the resend button below it.
        setUnconfirmed(true);
        return setError(
          es
            ? 'Su cuenta existe, pero el correo aún no está confirmado. Confírmelo para iniciar sesión.'
            : 'Your account exists, but the email address is not confirmed yet. Confirm it to sign in.',
        );
      }
      return setError(t('auth.errCreds'));
    }

    // Success bookkeeping must never block the buyer from moving on.
    void reportAuthSuccess('login', email).catch(() => undefined);
    setBusy(false);
    // Straight back to the exact lead / plan checkout when there is one.
    // The remembered intent is only used when this sign-in IS part of a
    // purchase (?buy=), so an ordinary header sign-in never springs a stale
    // checkout on someone. localizeNext() refuses off-site targets AND keeps
    // the buyer in the language they are using now (EN <-> ES).
    navigate(localizeNext(nextParam || (buy ? readPurchaseIntent()?.returnTo : null), path, path('/dashboard')));
  };

  /** One reset email per tap — double taps used to send two and burn two checks. */
  const [resetBusy, setResetBusy] = useState(false);
  const reset = async () => {
    if (resetBusy) return;
    setError('');
    setNote('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(es ? 'Escriba su correo arriba y toque otra vez.' : 'Type your email above, then tap again.');
      document.getElementById('login-email')?.focus();
      return;
    }
    const pendingMsg = captchaPendingMessage(captchaStatus, captcha, lang);
    if (pendingMsg) return setError(pendingMsg);
    setResetBusy(true);
    try {
      const verdict = await guardAuth('reset', email, captcha);
      setCaptchaReset((n) => n + 1);
      if (!verdict.ok) return setError(guardMessage(verdict, t, lang));
      await resetPassword(email);
      setNote(t('auth.resetSent'));
    } catch {
      setError(es ? 'No se pudo enviar. Intente de nuevo.' : 'Could not send it. Please try again.');
    } finally {
      setResetBusy(false);
    }
  };

  /** Carry a typed email to the sign-up page so nobody types it twice. */
  const emailExtra = /^\S+@\S+\.\S+$/.test(email) ? `email=${encodeURIComponent(email)}` : '';
  /** Email arrived pre-filled (post-payment / "already registered" hop): jump to the password. */
  const emailPrefilled = !!(params.get('email') || '').trim();

  return (
    <div className="mx-auto max-w-md px-4 py-10 sm:px-6 sm:py-14">
      <Seo title={t('auth.loginTitle')} description={t('auth.loginSub')} noindex />
      {buy && <PurchaseSteps buy={buy} lang={lang} signIn />}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h1 className="text-2xl font-extrabold text-slate-900">{t('auth.loginTitle')}</h1>
        <p className="mt-1 text-sm text-slate-500">{t('auth.loginSub')}</p>
        <form onSubmit={submit} noValidate className="mt-6 space-y-4">
          <div>
            <label htmlFor="login-email" className={labelCls}>{t('common.email')}</label>
            <input id="login-email" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} className={inputCls} value={email} onChange={(e) => setEmail(e.target.value.trim())} />
          </div>
          <div>
            <label htmlFor="login-password" className={labelCls}>{t('auth.password')}</label>
            {/* Show/hide: a hidden, mistyped password on a phone keyboard is the
                most common reason a returning buyer cannot get back in. */}
            <div className="relative">
              <input id="login-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" autoFocus={emailPrefilled} className={`${inputCls} pr-20`} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-0 px-3 text-xs font-bold text-slate-600 hover:text-slate-900">
                {showPw ? (es ? 'Ocultar' : 'Hide') : (es ? 'Mostrar' : 'Show')}
              </button>
            </div>
          </div>
          <Turnstile
            action="login"
            onVerify={setCaptcha}
            onStatus={setCaptchaStatus}
            resetKey={captchaReset}
            label={t('auth.captchaLabel')}
          />
          {error && (
            <div
              role="alert"
              className={`rounded-lg px-3 py-2 text-sm ${
                unconfirmed ? 'bg-amber-50 text-amber-900' : 'bg-red-50 text-red-700'
              }`}
            >
              <p className={unconfirmed ? 'flex items-start gap-2 font-semibold' : ''}>
                {unconfirmed && <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />}
                {error}
              </p>
              {/* Wrong password? The fix is right here, not at the bottom. */}
              {!unconfirmed && error === t('auth.errCreds') && (
                <button type="button" onClick={reset} className="mt-1.5 font-bold underline">
                  {es ? 'Enviarme un enlace para cambiar la contraseña' : 'Email me a link to reset my password'}
                </button>
              )}
            </div>
          )}
          {note && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{note}</p>}
          <button type="submit" disabled={busy} className="w-full rounded-xl bg-slate-900 px-4 py-3.5 text-base font-bold text-white hover:bg-slate-800 disabled:opacity-50">
            {busy
              ? t('common.loading')
              : buy
              ? es
                ? 'Iniciar sesión y continuar al pago'
                : 'Sign in & continue to payment'
              : t('nav.login')}
          </button>
        </form>

        {/*
          SELF-SERVICE RECOVERY.
          Shown as soon as an unconfirmed address is detected, and also offered
          quietly to anyone who typed an address — people who never received
          the first email should not have to guess or email support.
        */}
        {unconfirmed ? (
          <div className="mt-5 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-900">
              {es ? 'No recibió el correo de confirmación?' : 'Did not get the confirmation email?'}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-amber-800">
              {es
                ? 'Se lo enviamos de nuevo a esta dirección. Revise también la carpeta de spam.'
                : 'We will send it again to this address. Check your spam folder too.'}
            </p>
            <ResendConfirmation email={email} lang={lang} variant="inline" className="mt-3" />
          </div>
        ) : (
          /* Less clutter at the payment step: a buyer who never confirmed gets the
             amber resend box above automatically after one sign-in attempt. */
          !buy && (
            <details className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <summary className="cursor-pointer text-sm font-bold text-slate-700">
                {es ? '¿No recibió el correo de confirmación?' : 'Did not receive your confirmation email?'}
              </summary>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {es
                  ? 'Escriba su correo arriba y vuelva a enviarlo. Solo funciona para cuentas que ya existen.'
                  : 'Enter your email above and send it again. This only works for accounts that already exist.'}
              </p>
              <ResendConfirmation email={email} lang={lang} variant="inline" className="mt-3" />
            </details>
          )
        )}

        <button
          type="button"
          onClick={reset}
          disabled={resetBusy}
          className="mt-4 min-h-[44px] w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-60"
        >
          {resetBusy ? t('common.loading') : t('auth.forgot')}
        </button>

        {/* The `next` / `buy` intent MUST survive the hop to sign-up — dropping
            it here is exactly how new buyers lost the lead they were buying.
            The typed email travels too, so it is not typed twice. */}
        {buy ? (
          <Link
            to={authLink(path('/register'), nextParam || '', buy, emailExtra)}
            className="mt-5 flex min-h-[48px] w-full items-center justify-center rounded-xl border-2 border-orange-300 bg-orange-50 px-4 py-3 text-sm font-bold text-orange-700 hover:bg-orange-100"
          >
            {es ? '¿Primera vez? Crear cuenta gratis' : 'First time here? Create a free account'}
          </Link>
        ) : (
          <p className="mt-5 text-center text-sm text-slate-500">
            {t('auth.noAccount')}{' '}
            <Link to={authLink(path('/register'), nextParam || '', undefined, emailExtra)} className="font-bold text-orange-600">
              {t('nav.signup')}
            </Link>
          </p>
        )}

      </div>
    </div>
  );
};

export default Login;
